import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditNetworkSiteComposer } from "@/forms/sensnet/networkSites"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { useToast } from "@/contexts/ToastContext"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { MapPin, Loader2 } from "lucide-react"

function EditNetworkSitePage() {
  const { networkSiteNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading } = useApiQuery("getNetworkSiteByNumber", { networkSiteNumber })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = (data as any)?.content?.networkSite

  const handleBack = () => navigate({ to: '/sensnet/network-sites' })

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const corridorId = s ? (s.corridorId || s.networkCorridorNumber?.toString() || '') : ''
  const networkTechnologies = s ? (s.networkSiteTechnologies || s.networkTechnologies || []) : []

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0 overflow-hidden">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <MapPin className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Network Site</CardTitle>
              <CardDescription className="text-base">Update network site details</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          {s ? (
            <EditNetworkSiteComposer
              // Data-versioned key: re-mount when a fresh copy arrives so the form
              // re-initializes instead of showing previously-cached values.
              key={`site-${s.networkSiteNumber}-${(s as any)?.updatedAt ?? ''}`}
              networkSiteNumber={s.networkSiteNumber || 0}
              initialValues={{
                corridorId,
                name: s.name || s.networkSiteName || '',
                siteAddress: s.siteAddress || s.networkSiteAddress || '',
                siteGeoLocation: s.siteGeoLocation || s.location || { latitude: 0, longitude: 0 },
                locationNotes: s.locationNotes || '',
                indoorOrOutdoorSite: s.indoorOrOutdoorSite || 'outdoor',
                verticalStructureHeight: s.verticalStructureHeight,
                outdoorVerticalStructureType: s.outdoorVerticalStructureType,
                indoorStructureType: s.indoorStructureType,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                selectedTechnologies: Array.isArray(networkTechnologies)
                  ? (networkTechnologies as any[])
                      .map((t) => typeof t === 'string' ? t : t.technology)
                      .filter((t: string): t is '4G' | '5G' | 'LoRa' | 'Wifi' | 'RTK' =>
                        (['4G', '5G', 'LoRa', 'Wifi', 'RTK'] as string[]).includes(t)
                      )
                  : [],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                networkTechnologies: Array.isArray(networkTechnologies)
                  ? (networkTechnologies as any[]).filter((t) => typeof t === 'object' && t !== null)
                  : [],
                backhaulCapability: s.backhaulCapability,
                shelterAvailable: s.shelterAvailable,
                cabinetAttached: s.cabinetAttached,
                sitePictures: (s.sitePictures || []) as unknown as File[],
                networkSiteLaunchDate: s.networkSiteLaunchDate
                  ? new Date(s.networkSiteLaunchDate * 1000).toISOString().split('T')[0]
                  : undefined,
              }}
              corridorId={corridorId}
              submitLabel="Save Network Site"
              showHeader={false}
              onSuccess={handleBack}
              onError={(error) => toast({ title: 'Error', description: `Failed to update site: ${error.message}`, type: 'error' })}
              onCancel={handleBack}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Network site not found.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/sensnet/network-sites/$networkSiteNumber_/edit")({
  staticData: {
    title: 'Edit Network Site',
    isSecured: true,
  },
  component: EditNetworkSitePage,
})
